import { supabase } from '../supabase/client';
import { logActivity } from './activityService';
import type { CustomerSearchFilters, TransactionFilters } from '@/types/customer';

/**
 * Search customers by phone, name, email, Emirates ID, or account number
 */
export const searchCustomers = async (query: string, filters?: CustomerSearchFilters) => {
  try {
    let dbQuery = supabase
      .from('users')
      .select(`
        *,
        accounts:accounts(count),
        open_cases:support_cases!user_id(count)
      `)
      .limit(50);

    // Search query
    if (query && query.trim()) {
      const searchTerm = query.trim();
      
      // If it looks like a phone number
      if (searchTerm.startsWith('+') || /^\d+$/.test(searchTerm)) {
        dbQuery = dbQuery.ilike('phone_number', `%${searchTerm}%`);
      } 
      // Otherwise search by name, email, or Emirates ID
      else {
        dbQuery = dbQuery.or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,user_email.ilike.%${searchTerm}%,emirates_id.ilike.%${searchTerm}%`);
      }
    }

    // Apply filters
    if (filters?.status) {
      dbQuery = dbQuery.eq('account_status', filters.status);
    }

    if (filters?.country) {
      dbQuery = dbQuery.eq('country', filters.country);
    }

    if (filters?.nationality) {
      dbQuery = dbQuery.eq('nationality', filters.nationality);
    }

    const { data, error, count } = await dbQuery;

    if (error) {
      console.error('[CustomerService] Search error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data, count };
  } catch (error: any) {
    console.error('[CustomerService] Unexpected search error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get customer by ID
 */
export const getCustomerById = async (id: string, staffUserId: string) => {
  try {
    const { data, error } = await (supabase
      .from('users')
      .select('*')
      .eq('id', id)
      .single()) as any;

    if (error) {
      console.error('[CustomerService] Get customer error:', error);
      return { success: false, error: error.message };
    }

    // Log activity
    await logActivity({
      staff_user_id: staffUserId,
      action_type: 'view_customer',
      entity_type: 'customer',
      entity_id: id,
    });

    return { success: true, data };
  } catch (error: any) {
    console.error('[CustomerService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get customer accounts
 */
export const getCustomerAccounts = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('accounts')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('[CustomerService] Get accounts error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error: any) {
    console.error('[CustomerService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get customer cards
 */
export const getCustomerCards = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('cards')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('[CustomerService] Get cards error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error: any) {
    console.error('[CustomerService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get customer transactions
 */
export const getCustomerTransactions = async (
  userId: string,
  filters?: TransactionFilters,
  limit: number = 50
) => {
  try {
    // First get customer's account IDs
    const accountsResult = await getCustomerAccounts(userId);
    
    if (!accountsResult.success || !accountsResult.data) {
      return { success: false, error: 'Failed to fetch accounts' };
    }

    const accountIds = (accountsResult.data as any[]).map((acc: any) => acc.id);

    if (accountIds.length === 0) {
      return { success: true, data: [] };
    }

    let query = supabase
      .from('transactions')
      .select('*')
      .in('account_id', accountIds)
      .order('transaction_timestamp', { ascending: false })
      .limit(limit);

    // Apply filters
    if (filters?.transaction_type) {
      query = query.eq('transaction_type', filters.transaction_type);
    }

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    if (filters?.date_from) {
      query = query.gte('transaction_timestamp', filters.date_from);
    }

    if (filters?.date_to) {
      query = query.lte('transaction_timestamp', filters.date_to);
    }

    if (filters?.min_amount !== undefined) {
      query = query.gte('amount', filters.min_amount);
    }

    if (filters?.max_amount !== undefined) {
      query = query.lte('amount', filters.max_amount);
    }

    const { data, error } = await query;

    if (error) {
      console.error('[CustomerService] Get transactions error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error: any) {
    console.error('[CustomerService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get customer beneficiaries
 */
export const getCustomerBeneficiaries = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('beneficiaries')
      .select('*')
      .eq('user_id', userId)
      .order('is_favorite', { ascending: false })
      .order('contact_name', { ascending: true });

    if (error) {
      console.error('[CustomerService] Get beneficiaries error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error: any) {
    console.error('[CustomerService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get customer support cases
 */
export const getCustomerCases = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('support_cases')
      .select(`
        *,
        assigned_to_staff:staff_users!assigned_to(first_name, last_name, email)
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[CustomerService] Get cases error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error: any) {
    console.error('[CustomerService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get customer devices
 */
export const getCustomerDevices = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('devices')
      .select('*')
      .eq('user_id', userId)
      .order('last_used_at', { ascending: false });

    if (error) {
      console.error('[CustomerService] Get devices error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error: any) {
    console.error('[CustomerService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get customer sessions
 */
export const getCustomerSessions = async (userId: string, limit: number = 20) => {
  try {
    const { data, error } = await supabase
      .from('sessions')
      .select(`
        *,
        device:devices(device_name, device_type, os_name)
      `)
      .eq('user_id', userId)
      .order('start_time', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('[CustomerService] Get sessions error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error: any) {
    console.error('[CustomerService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

export const customerService = {
  searchCustomers,
  getCustomerById,
  getCustomerAccounts,
  getCustomerCards,
  getCustomerTransactions,
  getCustomerBeneficiaries,
  getCustomerCases,
  getCustomerDevices,
  getCustomerSessions,
};

